import { MenuItemMissingData, MenuItemStatusFilter } from '@/generated/gql/graphql'

import { parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import {
  getMenuItemTranslatedValue,
  type MenuItemTranslation,
} from '../../catering/supplier-menu/utils/getMenuItemTranslatedValue'
import { SERVICE_OPTIONS } from './filterOptions'
import type { ActiveDateState, MissingFilter, ServiceFilter, StatusFilter } from './types'

// Format a single price (pence → "<symbol>12.34"). Card components use this.
export const formatPrice = (pence: number, currencySymbol: string): string =>
  `${currencySymbol}${(pence / 100).toFixed(2)}`

// "3 items" / "1 item" — shared by the review dialogs, bulk bar, and toasts.
export const formatCount = (n: number, singular: string, plural?: string) =>
  `${n} ${n === 1 ? singular : (plural ?? `${singular}s`)}`

export type MenuItemCardData = {
  id: string
  name: string
  description: string
  image: string | null
  isOption: boolean
  isMenuItem: boolean
  price: number // pence (vendor-facing, inc. tax)
  priceExTax: number | null // pence (vendor-facing, ex. tax)
  taxRate: number | null
  dietaries: string[]
  allergens: string[]
  /** One entry per configured service, in display order. Absent services
      (no variation of that varType) are simply not listed. */
  services: ServiceAvailability[]
  servings: number | null
  /** Raw active flag — the footer shows this even for private items (privacy
      is denoted solely by the lock badge; `status` keeps the private override
      for filtering). */
  isActive: boolean
  status: 'active' | 'inactive' | 'private'
  /** Set when active dates make an item unorderable today — drives the
      Expired/Scheduled tag. Independent of `isActive`/`status`, which keep
      showing the raw flag. */
  activeDateState: ActiveDateState | null
  /** Raw endpoints, kept for the tag's tooltip date. */
  activeFrom: string | null
  activeTo: string | null
  isOptionFor: { id: string; name: string }[]
  /** Draft lock state (null on live items). With requestToDelete, drives the
      In Review tab's "Unlocked for vendor" chip. */
  locked: boolean | null
  requestToDelete: boolean
  requestDeleteTimestamp: string | null
}

type RawVariation = {
  varType?: string | null
  deleted?: boolean | null
  type?: string | null
  isActive?: boolean | null
  servings?: number | null
  isPrivate?: boolean | null
}

type RawItem = {
  _id: string
  name?: string | null
  shortDescription?: string | null
  description?: string | null
  image?: string | null
  isActive?: boolean | null
  activeFrom?: string | null
  activeTo?: string | null
  isMenuItem?: boolean | null
  isOption?: boolean | null
  price?: number | null
  priceExTax?: number | null
  taxRate?: number | null
  dietaries?: string[]
  allergens?: string[]
  variations?: RawVariation[] | null
  isOptionFor?: { _id: string; name?: string | null }[] | null
  locked?: boolean | null
  requestToDelete?: boolean | null
  requestDeleteTimestamp?: string | null
  translations?: MenuItemTranslation[] | null
}

// MenuItemVariation.varType is an unconstrained string in the graph; these are
// the only two values the platform writes. Compare through this map rather than
// inline literals so the vocabulary is controlled in one place.
export const VAR_TYPE = {
  catering: 'gm',
  canteen: 'cc',
} as const

// 'heldByItem' = the channel is switched on but the item itself is Inactive, so
// nothing is orderable; the card greys it like 'inactive' but explains why (UAT).
export type ServiceStatus = 'active' | 'inactive' | 'heldByItem'

export type ServiceAvailability = {
  service: ServiceFilter
  status: ServiceStatus
}

// Ordered by SERVICE_OPTIONS so every card lists channels the same way.
export function getServiceAvailability(
  variations: RawVariation[] | null | undefined,
  itemIsActive: boolean,
): ServiceAvailability[] {
  return SERVICE_OPTIONS.flatMap(({ value: service }) => {
    const live = (variations ?? []).filter((v) => v.varType === service && v.deleted !== true)
    if (live.length === 0) {
      return []
    }

    const configuredActive = live.some((v) => v.isActive === true)
    const status = !configuredActive ? 'inactive' : itemIsActive ? 'active' : 'heldByItem'

    return [{ service, status }]
  })
}

/**
 * Why an item with active dates isn't orderable today, or null if it is.
 *
 * Compares calendar days in EUROPE/LONDON because that is what the backend
 * judges against — feedr-menu pins `moment.tz.setDefault('Europe/London')`
 * (lib/app.ts), so its startOf/endOf day are London midnights, not UTC or the
 * viewer's. Under BST these differ by an hour and the stored endpoints sit
 * exactly on that seam (a 7 Sep start is stored 2026-09-06T23:00Z), so
 * comparing in UTC — or in the browser's zone — drops or invents a tag.
 * An endpoint landing ON today is still orderable, matching the backend's
 * inclusive $lte/$gte.
 */
const MENU_TIME_ZONE = 'Europe/London'

// en-CA renders as YYYY-MM-DD, so the date parts sort lexically.
const toZonedDay = (date: Date): string =>
  date.toLocaleDateString('en-CA', { timeZone: MENU_TIME_ZONE })

export const getActiveDateState = (
  activeFrom: string | null | undefined,
  activeTo: string | null | undefined,
  now: Date = new Date(),
): ActiveDateState | null => {
  const today = toZonedDay(now)

  if (activeTo) {
    const to = parseISO(activeTo)
    if (!Number.isNaN(to.getTime()) && toZonedDay(to) < today) return 'expired'
  }
  if (activeFrom) {
    const from = parseISO(activeFrom)
    if (!Number.isNaN(from.getTime()) && toZonedDay(from) > today) return 'scheduled'
  }
  return null
}

/** Tag tooltip naming the endpoint behind it; '' when the date is unusable. */
export const getActiveDateTooltip = (item: {
  activeDateState: ActiveDateState | null
  activeFrom: string | null
  activeTo: string | null
}): string => {
  if (!item.activeDateState) return ''
  const expired = item.activeDateState === 'expired'
  const raw = expired ? item.activeTo : item.activeFrom
  if (!raw) return ''
  const date = parseISO(raw)
  if (Number.isNaN(date.getTime())) return ''
  // Same zone as the tag's own comparison, or a 23:00Z endpoint reads a day early.
  const label = formatInTimeZone(date, MENU_TIME_ZONE, 'd MMM yyyy')
  return `${expired ? 'Active until' : 'Active from'} ${label}`
}

/**
 * Derive the card status from the item's active/private state.
 * isPrivate (from any variation) → 'private'; else isActive → 'active'/'inactive'.
 */
const deriveStatus = (isActive: boolean, isPrivate: boolean): 'active' | 'inactive' | 'private' => {
  if (isPrivate) return 'private'
  return isActive ? 'active' : 'inactive'
}

/**
 * Map a raw GraphQL MenuItem to the flat MenuItemCardData shape the card renders.
 * Name/description resolve through the same translations helper as the preview
 * modal (PT-1859) so both surfaces show identical text; root fields are the
 * fallback. isOptionFor names stay raw — they belong to other items whose
 * translations the list query doesn't fetch.
 */
export const mapToCardData = (item: RawItem, locale?: string): MenuItemCardData => {
  const variations = item.variations ?? []
  const isActive = item.isActive ?? false
  // isPrivate lives on variations, not on the MenuItem root.
  const isPrivate = variations.some((v) => v.isPrivate === true)
  // First variation with a non-null servings value.
  const firstServings = variations.find((v) => v.servings != null)?.servings ?? null

  const translated = (key: string, fallback?: string | null) =>
    getMenuItemTranslatedValue({ translations: item.translations, locale, key, fallback })

  return {
    id: item._id,
    name: translated('name', item.name),
    description:
      translated('shortDescription', item.shortDescription) ||
      translated('description', item.description),
    // Items without an image store '' (not null) — normalise so fallbacks
    // (?? / placeholder) treat them as missing.
    image: item.image || null,
    isOption: item.isOption ?? false,
    isMenuItem: item.isMenuItem ?? false,
    price: item.price ?? 0,
    priceExTax: item.priceExTax ?? null,
    taxRate: item.taxRate ?? null,
    dietaries: (item.dietaries ?? []) as string[],
    allergens: (item.allergens ?? []) as string[],
    services: getServiceAvailability(variations, isActive),
    servings: firstServings,
    isActive,
    status: deriveStatus(isActive, isPrivate),
    // Only an otherwise-Active item needs the reason: on an already-Inactive
    // card the flag is the explanation, and a second tag just adds noise.
    activeDateState: isActive ? getActiveDateState(item.activeFrom, item.activeTo) : null,
    activeFrom: item.activeFrom ?? null,
    activeTo: item.activeTo ?? null,
    isOptionFor: (item.isOptionFor ?? []).map((opt) => ({
      id: opt._id,
      name: opt.name ?? '',
    })),
    locked: item.locked ?? null,
    requestToDelete: item.requestToDelete ?? false,
    requestDeleteTimestamp: item.requestDeleteTimestamp ?? null,
  }
}

/**
 * CC-only items can't render in the marketplace (GM) item modal — its fragment
 * fetches gm variations only. The preview routes them to the canteen modal.
 */
export const isCanteenOnlyCard = (item: MenuItemCardData): boolean => {
  const services = item.services.map((s) => s.service)
  return services.includes(VAR_TYPE.canteen) && !services.includes(VAR_TYPE.catering)
}

// MissingFilter values match the enum's wire values 1:1, but map through an
// explicit record (mirroring SERVICE_FILTER_TO_ENUM) rather than casting.
const MISSING_FILTER_TO_ENUM: Record<MissingFilter, MenuItemMissingData> = {
  nutritional: MenuItemMissingData.Nutritional,
  foodGoals: MenuItemMissingData.FoodGoals,
  images: MenuItemMissingData.Images,
}

// [] ⇒ undefined so the filter key is omitted from MenuItemFiltersInput entirely.
export const toMissingDataFilter = (missing: MissingFilter[]): MenuItemMissingData[] | undefined =>
  missing.length > 0 ? missing.map((value) => MISSING_FILTER_TO_ENUM[value]) : undefined

const STATUS_FILTER_TO_ENUM: Record<StatusFilter, MenuItemStatusFilter> = {
  active: MenuItemStatusFilter.Active,
  inactive: MenuItemStatusFilter.Inactive,
  private: MenuItemStatusFilter.Private,
}

export const toStatusesFilter = (statuses: StatusFilter[]): MenuItemStatusFilter[] | undefined =>
  statuses.length > 0 ? statuses.map((value) => STATUS_FILTER_TO_ENUM[value]) : undefined
